import { useEffect, useState } from "react";
import { Alert, Container, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import iconApps from "../../assets/images/icon/icon.png";
import { verifyInvitationToken } from "../../api/InvitationApi";
import { Trans, useTranslation } from "react-i18next";
import LanguageSwitcher from "../../components/LanguageSwitcher";

const REDIRECT_SECONDS = 5;

const VerifyInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [countdown, setCountdown] = useState(null);

  const token = searchParams.get("token") || "";

  useEffect(() => {
    let cancelled = false;

    const runVerification = async () => {
      if (!token) {
        setStatus("error");
        setMessage(t("verify_invitation.token_missing"));
        setIsLoading(false);
        return;
      }

      const response = await verifyInvitationToken(token);

      if (cancelled) {
        return;
      }

      if (response?.status === 200) {
        setStatus("success");
        setMessage(response.message || t("verify_invitation.success"));
        setCountdown(REDIRECT_SECONDS);
      } else {
        setStatus("error");
        setMessage(response?.message || t("verify_invitation.failed"));
      }

      setIsLoading(false);
    };

    runVerification();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (status !== "success" || countdown === null) {
      return;
    }

    if (countdown <= 0) {
      navigate("/login");
      return;
    }

    const timer = window.setTimeout(() => {
      setCountdown((prev) => (prev === null ? prev : prev - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [countdown, status, navigate]);

  return (
    <>
      <Helmet>
        <title>{t("verify_invitation.meta_title")}</title>
      </Helmet>

      <LanguageSwitcher placement="floating" syncServer={false} />
      <div className="center-content">
        <Container className="box-shadow">
          <img
            src={iconApps}
            alt={t("logo_alt")}
            style={{
              marginTop: "20px",
              marginBottom: "20px",
              width: "100px",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />

          <h2 className="text-center">{t("verify_invitation.title")}</h2>

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
              <p className="mt-3 mb-0">{t("verify_invitation.loading")}</p>
            </div>
          ) : (
            <>
              <Alert variant={status === "success" ? "success" : "danger"}>
                {(message || "").replace(/<br\s*\/?>/gi, " ")}
              </Alert>

              {status === "success" && (
                <p className="text-center mb-3">
                  <Trans t={t} i18nKey="verify_invitation.redirect_console" values={{ count: countdown }} components={{ b: <b /> }} />
                </p>
              )}

              <p className="text-center mt-3 mb-0">
                {t("verify_invitation.continue_to")}{" "}
                <Link to="/login">{t("verify_invitation.login")}</Link>
              </p>
            </>
          )}
        </Container>
      </div>
    </>
  );
};

export default VerifyInvitationPage;
